// src/hooks/useRefetchOnAppResume.ts
// Au retour dans l’app après une pause, invalide les queries actives (léger, pas de reload).

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/** Pause minimale hors app avant un refetch soft (évite le thrash en multi-tâche). */
const MIN_HIDDEN_MS = 60_000;

/**
 * Routes où un refetch auto est dangereux (panier / encaissement en mémoire locale).
 * Le bouton navbar « Actualiser » reste disponible si l’utilisateur le demande.
 */
function isCashierRoute(pathname: string): boolean {
  return (
    pathname === '/pos/transaction' ||
    pathname.startsWith('/pos/transaction/')
  );
}

/**
 * Quand l’onglet/PWA redevient visible après ≥ 60 s en arrière-plan,
 * marque les queries comme stale → refetch uniquement celles encore montées.
 * Ne remplace pas la bannière SW (déploiements).
 * Désactivé sur la caisse pour ne pas perturber un encaissement en cours.
 *
 * Utilise `window.location` (pas `useLocation`) car le hook peut vivre
 * hors de `<BrowserRouter>` (ex. AppWithUpdateCheck).
 */
export function useRefetchOnAppResume(): void {
  const queryClient = useQueryClient();
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt == null) return;
      if (Date.now() - hiddenAt < MIN_HIDDEN_MS) return;
      if (isCashierRoute(window.location.pathname)) return;
      void queryClient.invalidateQueries();
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [queryClient]);
}
